/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/**
 *
 * SplitSchemeConfiguration
 *
 */

import React, { memo, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_OPTIONS,
  PLACEHOLDER_TEXT,
  DATE_AND_TIME_FORMAT,
} from 'utils/constants';
import { copyToClipboard } from 'utils/common';
import EVENTS from 'constants/analytics';
import Analytics from 'utils/analytics';
import Loader from 'components/Loader';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import {
  Conditional,
  Space,
  Text,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  WithPagination,
} from '@cashfree-intl/coherent';
import moment from 'moment';
import TableDataNotFound from 'components/TableDataNotFound';
import CustomIcon from 'components/CustomIcons';
import makeSelectSplitSchemeConfiguration from './selectors';
import reducer from './reducer';
import saga from './saga';
import {
  DELETE_MODAL,
  DYNAMIC,
  EDIT_MODAL,
  FIXED,
  HEADINGS,
  PRODUCT_MAPPER,
  SPLIT_MODAL,
} from './constants';
import {
  enableLoaderAction,
  searchConfigurationsAction,
  updateFiltersAction,
  updateTableDataAction,
  createSchemeAction,
  deleteSchemeAction,
  getTerminalIdAction,
  verifyVendorByIdAction,
  getStaticSplitStatusAction,
} from './actions';
import { MerchantContext } from '../../contexts/MerchantContext';
import SplitModal from './components/SplitModal';
import DeleteModal from './components/DeleteModal';
import EditModal from './components/EditModal';

export function SplitSchemeConfiguration({
  getStaticSplitStatus,
  searchConfigurations,
  updateTableData,
  updateFilters,
  enableLoader,
  createScheme,
  deleteScheme,
  getTerminalIdList,
  verifyVendorId,
  splitSchemeConfiguration,
}) {
  useInjectReducer({ key: 'splitSchemeConfiguration', reducer });
  useInjectSaga({ key: 'splitSchemeConfiguration', saga });

  const { easySplitEnabled } = useContext(MerchantContext);

  const {
    staticSplitEnabled,
    data,
    isLoading,
    activeNavigation,
    hasPrev,
    hasNext,
    currentPageRowData,
    filters,
    filtersForDisplay,
    terminalIdList,
    productList,
  } = splitSchemeConfiguration;

  const [searchParams] = useSearchParams();
  const env = searchParams.get('env');

  const handleBackPress = () => {
    window.location.href = `${
      process.env.COMMON_BASE_PATH
    }/settings?env=${env || 'prod'}`;
  };

  const [openModal, setOpenModal] = useState('');
  const [modalData, resetModalData] = useState({});

  const renderWithCopyToClipboard = value => (
    <div className="flex copy-to-clipboard-wrapper">
      <span className="table-content">{value || PLACEHOLDER_TEXT}</span>
      <Conditional if={value && value !== 'N/A'}>
        <CustomIcon
          componentName="COPY_TO_CLIPBOARD"
          handleClick={copyToClipboard(value)}
        />
      </Conditional>
    </div>
  );

  useEffect(() => {
    getStaticSplitStatus();
  }, []);

  useEffect(() => {
    getTableData();
  }, [filters, openModal]);

  useEffect(() => {
    getTableData();
    switch (openModal) {
      case SPLIT_MODAL: {
        Analytics.track(EVENTS.SPLIT_SCHEME_CONFIGURATION.SPLIT_MODAL);
        break;
      }
      case EDIT_MODAL: {
        Analytics.track(EVENTS.SPLIT_SCHEME_CONFIGURATION.EDIT_MODAL);
        break;
      }
      default:
        break;
    }
  }, [openModal]);

  useEffect(() => {
    if (!isLoading) {
      const totalCount = data.length;
      const pageData = JSON.parse(JSON.stringify(data));
      let formattedData = [];

      formattedData = pageData.map(rowData => ({
        ...rowData,
        createdAt: moment(rowData.addedOn).format(DATE_AND_TIME_FORMAT),
        product: () =>
          rowData.productType
            ? PRODUCT_MAPPER[rowData.productType]
            : PLACEHOLDER_TEXT,
        terminalRefId: () =>
          rowData.productType === 'SBC'
            ? renderWithCopyToClipboard(rowData.terminalRefId)
            : PLACEHOLDER_TEXT,
        terminalId: () =>
          rowData.productType === 'SBC'
            ? renderWithCopyToClipboard(rowData.terminalId)
            : PLACEHOLDER_TEXT,
        splitType: rowData.active ? FIXED : DYNAMIC,
        scheme: () =>
          rowData.scheme.length > 0 ? (
            <div className="split-scheme">
              {rowData.scheme.map(scheme => (
                <div key={rowData.merchantVendorId}>
                  <div className="split-scheme-item">
                    {scheme.merchantVendorId}
                  </div>
                  <div className="split-scheme-item">-</div>
                  <div className="split-scheme-item">{scheme.percentage}%</div>
                </div>
              ))}
            </div>
          ) : (
            PLACEHOLDER_TEXT
          ),
        button: (
          <div className="row-buttons">
            <div
              className="table-cta-item"
              onClick={() => {
                setOpenModal(EDIT_MODAL);
                resetModalData(rowData);
              }}
            >
              <CustomIcon componentName="EDIT" />
            </div>
            {/* <div
              className="table-cta-item"
              onClick={() => {
                setOpenModal(DELETE_MODAL);
                resetModalData(rowData);
              }}
            >
              <CustomIcon componentName="DELETE_SPLIT" />
            </div> */}
          </div>
        ),
      }));

      const hasPrevEnabled = filters.pageNo !== 1;
      const hasNextEnabled = totalCount >= filters.pageSize;

      updateTableData({
        totalCount,
        currentPageRowData: formattedData,
        hasPrevEnabled,
        hasNextEnabled,
      });
    }
  }, [isLoading, data]);

  const getTableData = (f = filters) => {
    // make api call
    enableLoader();
    const { pageNo, pageSize } = f;

    const formattedFilterObj = {
      pageNo: pageNo - 1,
      pageSize,
      active: true,
    };

    searchConfigurations({
      options: formattedFilterObj,
    });
  };

  const onLimitChange = (e, limitChangeData) => {
    const options = {
      ...filters,
      pageNo: DEFAULT_CURRENT_PAGE,
      pageSize: parseInt(limitChangeData.value, 10),
      filtersForDisplay,
    };
    updateFilters(options);
    Analytics.track(EVENTS.SPLIT_SCHEME_CONFIGURATION.PAGE_SIZE, {
      pageSize: limitChangeData.value,
    });
  };

  const onPageChange = type => {
    const options = { ...filters, activeNavigation, filtersForDisplay };
    switch (type) {
      case 'NEXT':
        options.pageNo += 1;
        break;
      case 'PREV':
        options.pageNo -= 1;
        break;
      default:
        break;
    }
    updateFilters(options);
  };

  const closeModal = () => setOpenModal('');

  return (
    <div className="split-scheme-configuration">
      <Loader active={isLoading} />
      <Conditional if={easySplitEnabled && staticSplitEnabled}>
        <Space justifyContent="space-between" alignItems="center">
          <div className="back-navigate-wrapper">
            <>
              {/* eslint-disable-next-line */}
              <div className="back-navigate-left" onClick={handleBackPress}>
                <CustomIcon componentName="CHEVRONDOWN" direction="left" />
                Back
              </div>
              <div className="global-heading">Split Scheme Configuration</div>
            </>
          </div>
          <Button
            className="create-split-config"
            primary
            onClick={() => setOpenModal(SPLIT_MODAL)}
          >
            Create Split Configuration
          </Button>
        </Space>
        <Space
          justifyContent="space-between"
          alignItems="center"
          className="mb-3"
        >
          <Text as="span" color="bodyLight">
            Define the split configuration for your accounts in order to invoke
            the fixed splits at Cashfree Payments end.
          </Text>
        </Space>
        <WithPagination
          limit={filters.pageSize}
          limitOptions={DEFAULT_LIMIT_OPTIONS}
          hasPrev={hasPrev}
          hasNext={hasNext}
          currentPage={filters.pageNo}
          currentPageCount={currentPageRowData.length}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
        >
          <div className="common-table-wrapper">
            <Table fixed singleLine>
              <TableHeader>
                <TableRow>
                  {HEADINGS.map(heading => (
                    <TableHeaderCell
                      key={heading.key}
                      $colWidth={heading.width}
                      textAlign={heading.align}
                    >
                      {heading.displayName}
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentPageRowData.map(row => (
                  <TableRow key={row.id}>
                    {HEADINGS.map(heading => {
                      const cellValue = row[heading.key];

                      return (
                        <TableCell key={heading.key} textAlign={heading.align}>
                          {typeof cellValue === 'function' ? (
                            cellValue()
                          ) : (
                            <div className="flex">
                              <span className="table-content">{cellValue}</span>
                            </div>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!currentPageRowData.length && (
              <TableDataNotFound tab={activeNavigation} />
            )}
          </div>
        </WithPagination>
        <Conditional if={openModal === SPLIT_MODAL}>
          <SplitModal
            productList={productList}
            verifyVendorId={verifyVendorId}
            terminalIdList={terminalIdList}
            getTerminalIdList={getTerminalIdList}
            getConfigurationByTerminalId={searchConfigurations}
            onClose={closeModal}
            createScheme={createScheme}
          />
        </Conditional>
        <Conditional if={openModal === EDIT_MODAL}>
          <EditModal
            schemeData={modalData}
            verifyVendorId={verifyVendorId}
            onClose={closeModal}
            createScheme={createScheme}
          />
        </Conditional>
        <Conditional if={openModal === DELETE_MODAL}>
          <DeleteModal
            // open={openDeleteModal}
            onClose={closeModal}
            deleteScheme={() =>
              deleteScheme({ data: modalData, onClose: () => setOpenModal('') })
            }
          />
        </Conditional>
      </Conditional>
    </div>
  );
}

SplitSchemeConfiguration.propTypes = {
  getStaticSplitStatus: PropTypes.func.isRequired,
  splitSchemeConfiguration: PropTypes.object.isRequired,
  searchConfigurations: PropTypes.func.isRequired,
  updateTableData: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  createScheme: PropTypes.func.isRequired,
  deleteScheme: PropTypes.func.isRequired,
  getTerminalIdList: PropTypes.func.isRequired,
  verifyVendorId: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  splitSchemeConfiguration: makeSelectSplitSchemeConfiguration(),
});

export function mapDispatchToProps(dispatch) {
  return {
    getStaticSplitStatus: () => dispatch(getStaticSplitStatusAction()),
    searchConfigurations: data => dispatch(searchConfigurationsAction(data)),
    updateTableData: data => dispatch(updateTableDataAction(data)),
    updateFilters: data => dispatch(updateFiltersAction(data)),
    enableLoader: () => dispatch(enableLoaderAction()),
    createScheme: data => dispatch(createSchemeAction(data)),
    deleteScheme: data => dispatch(deleteSchemeAction(data)),
    getTerminalIdList: data => dispatch(getTerminalIdAction(data)),
    verifyVendorId: data => dispatch(verifyVendorByIdAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(SplitSchemeConfiguration);
